import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, StickyNote } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskCard } from "@/components/TaskCard";
import { GuideSheet } from "@/components/GuideSheet";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";

function NamePrompt({ onSave, initial }) {
  const [n, setN] = useState(initial || "");
  return (
    <form onSubmit={(e) => { e.preventDefault(); n.trim() && onSave(n.trim()); }} className="pl-card p-5 space-y-3 mb-5" data-testid="sitter-name-prompt">
      <p className="font-heading text-xl font-bold">Hi! What's your name?</p>
      <p className="text-sm text-slate-500">So the owners know who logged each task.</p>
      <input autoFocus className="pl-input" value={n} onChange={(e) => setN(e.target.value)} data-testid="sitter-view-name-input" />
      <button className="pl-btn-primary w-full" data-testid="sitter-view-name-save-btn">Continue</button>
    </form>
  );
}

export default function SitterView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState(() => localStorage.getItem(`pl_sitter_${token}`) || "");
  const load = useCallback(() => api.get(`/sitter/${token}`).then((r) => { setData(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))), [token]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const saveName = (n) => { localStorage.setItem(`pl_sitter_${token}`, n); setName(n); };
  const done = async (task) => {
    try {
      await api.post(`/sitter/${token}/log`, { schedule_id: task.schedule_id, time: task.time, name });
      toast.success(`Logged ${task.title} for ${task.pet_name}. The owners can see it now.`);
    } catch (e) { toast[e.response?.status === 409 ? "warning" : "error"](errMsg(e)); }
    load();
  };

  const ribbon = (
    <a href="/?utm_source=sitter" data-testid="sitter-powered-by-link" className="no-print block bg-[#1E293B] text-white text-center text-xs py-2 font-semibold">Powered by PackLog · try it free</a>
  );
  if (err) return <div className="min-h-screen bg-[#FAF7F2]">{ribbon}<div className="max-w-md mx-auto p-8 text-center"><Logo /><p className="mt-6 font-heading text-2xl font-bold" data-testid="sitter-link-error">{err}</p></div></div>;
  if (!data) return <div className="min-h-screen bg-[#FAF7F2]">{ribbon}</div>;
  const tasks = data.today.tasks;
  const todo = tasks.filter((t) => !t.done);
  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-16" data-testid="sitter-view-page">
      {ribbon}
      <div className="max-w-2xl mx-auto px-4 pt-5">
        <p className="text-sm font-semibold text-slate-500">{data.guide.household.name}</p>
        <h1 className="font-heading text-4xl font-extrabold">Hi{name ? ` ${name}` : ""}!</h1>
        <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Clock className="h-4 w-4" />Access until {new Date(data.expires_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
        {data.notes && <p className="pl-card p-3 mt-4 text-sm flex gap-2 bg-[#FFFBF0]"><StickyNote className="h-4 w-4 text-[#D97706] shrink-0 mt-0.5" />{data.notes}</p>}
        <div className="mt-5">
          {!name ? <NamePrompt onSave={saveName} initial={data.sitter_name} /> : (
            <Tabs defaultValue="today">
              <TabsList className="w-full grid grid-cols-2 h-12 rounded-2xl bg-[#EFECE6]">
                <TabsTrigger value="today" className="rounded-xl" data-testid="sitter-tab-today">Today ({todo.length} left)</TabsTrigger>
                <TabsTrigger value="guide" className="rounded-xl" data-testid="sitter-tab-guide">Care guide</TabsTrigger>
              </TabsList>
              <TabsContent value="today" className="mt-4 space-y-3">
                {tasks.length === 0 && <p className="text-slate-500">Nothing scheduled today.</p>}
                {todo.map((t) => <TaskCard key={t.key} idx={tasks.indexOf(t)} task={t} onDone={done} showDetails />)}
                {tasks.filter((t) => t.done).map((t) => <TaskCard key={t.key} idx={tasks.indexOf(t)} task={t} />)}
              </TabsContent>
              <TabsContent value="guide" className="mt-4"><GuideSheet guide={data.guide} /></TabsContent>
            </Tabs>
          )}
        </div>
        <p className="text-[11px] text-slate-400 mt-8">PackLog is a tracking tool and never gives medical advice. For any health concern, please contact the pet's veterinarian (listed in the care guide).</p>
      </div>
    </div>
  );
}
